// „Powtórka dnia” kursu v2 (patch-47, SPEC §23.13.2 pkt 5 i §23.27): które słowa kursu powtarzamy dziś i ile ich jeszcze zmieści się w dziennym limicie.
// Czysty kod bez DOM i sieci. Algorytm wyboru (`review.ts: planReviews`) i pudełka (`srs.ts`) już były — tu jest ich wpięcie w dane kursu:
//  • słowa pochodzą z `progress.course.items`, a dzień „wczoraj” z dziennika `progress.course.log`;
//  • sesja jest porcją najwyżej `REVIEW_PORTION` (10) słów; dzienny limit (`sessionSize`: 8 powtórek na każde nowe słowo dziennie, zaokrąglone do 5, 20–120)
//    to górna granica sumy porcji danego dnia — to, co się nie zmieściło, rośnie w kolejce (względne spóźnienie) i wraca jutro;
//  • ile słów dziecko powtórzyło dziś, wynika z danych (słowa z `lastSeen` = dziś, które nie są dzisiejszymi nowymi słowami), więc nie trzeba osobnego licznika
//    i dwa urządzenia po synchronizacji widzą ten sam stan;
//  • w wyborze uczestniczą tylko słowa, dla których lekcja jest w treści (słowo po usuniętej lekcji nie zapycha kolejki).
import { config as defaultConfig } from '../config.ts';
import type { Config } from '../config.ts';
import type { Progress, SrsItem } from '../types.ts';
import type { LessonV2 } from './levels.ts';
import { defaultReviewConfig, planReviews, sessionSize } from './review.ts';
import type { ReviewConfig, ReviewPlan } from './review.ts';
import { hashSeed, lessonOfWord } from './sessionV2.ts';

/** Ile słów ma jedna porcja powtórki (SPEC §23.13.2 pkt 5: „w porcjach po 10”). */
export const REVIEW_PORTION = 10;

export interface CourseReviewArgs {
  progress: Progress;
  lessons: readonly LessonV2[];
  today: string;
  /** Identyfikator profilu (ziarno losowania: ten sam profil i dzień = ten sam wybór przy remisach). */
  profile: string;
  cfg?: Config;
  review?: ReviewConfig;
}

/** Słowa kursu, które da się dziś ćwiczyć: mają lekcję w treści (inaczej nie zbudujemy ćwiczenia). */
export function reviewableItems(progress: Progress, lessons: readonly LessonV2[]): Record<string, SrsItem> {
  const course = progress.course;
  const out: Record<string, SrsItem> = {};
  if (!course) return out;
  for (const [w, it] of Object.entries(course.items)) if (lessonOfWord(w, lessons, course.done)) out[w] = it;
  return out;
}

/** Ile słów dziecko powtórzyło dziś: słowa z `lastSeen` = dziś, poza dzisiejszymi nowymi słowami (te mają `lastSeen` = dziś od lekcji). */
export function reviewedToday(progress: Progress, today: string): number {
  const course = progress.course;
  if (!course) return 0;
  const fresh = new Set(course.log?.[today]?.words ?? []);
  return Object.values(course.items).filter((it) => it.lastSeen === today && !fresh.has(it.word)).length;
}

/** Ile powtórek zostało dziś w dziennym limicie (limit sesji minus już powtórzone). */
export function reviewBudget(progress: Progress, today: string, review: ReviewConfig = defaultReviewConfig): number {
  return Math.max(0, sessionSize(progress.course?.log ?? {}, today, review) - reviewedToday(progress, today));
}

/** Plan jednej porcji „Powtórki dnia”: najwyżej `REVIEW_PORTION` słów, nie więcej niż zostało w dziennym limicie. */
export function planCourseReview(a: CourseReviewArgs): ReviewPlan {
  const cfg = a.cfg ?? defaultConfig;
  const review = a.review ?? defaultReviewConfig;
  const log = a.progress.course?.log ?? {};
  return planReviews({
    items: reviewableItems(a.progress, a.lessons),
    today: a.today,
    log,
    limit: Math.min(REVIEW_PORTION, reviewBudget(a.progress, a.today, review)),
    // dzisiejsze nowe słowa nie wchodzą do powtórki tego samego dnia (mają termin jutro, ale wykluczamy je wprost — także po „Powtórz lekcję”)
    exclude: new Set(log[a.today]?.words ?? []),
    seed: hashSeed(`${a.profile}|${a.today}|review`),
    intervals: cfg.srs.intervals,
    cfg: review,
  });
}

/** Ile słów pokazać na przycisku „Powtórka dnia (N)”: zaległe, ale nie więcej niż zostało w dziennym limicie; 0 = przycisku nie ma. */
export function courseReviewDue(a: CourseReviewArgs): number {
  const review = a.review ?? defaultReviewConfig;
  const plan = planCourseReview({ ...a, review });
  return Math.min(plan.dueTotal, reviewBudget(a.progress, a.today, review));
}
